import React, { useState, useMemo, useCallback } from "react";
import { createWorkbookFromTable } from "../../utils/table_export";
import * as XLSX from "xlsx";
import { observer } from "mobx-react-lite";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  HStack,
  Link,
  useToast,
  Collapse
} from "@chakra-ui/react";
import { useStores } from "../../store/store_context";
import { unique } from "jquery";
import ColumnFilters from "./column_filters";
import { saveAs } from "file-saver";

const MONTH_NAMES = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"
];

const YEARS_MAP = {
    "122629": "Сделка не закрыта",
    "4302": "2017",
    "4304": "2018",
    "4306": "2019",
    "4308": "2020",
    "122611": "2021",
    "134821": "2022",
    "153037": "2023",
    "153039": "2024",
    "483623": "2025",
    "483625": "2026",
    "483627": "2027",
    "483629": "2028",
}

const MONTH_MAP_B24 = {
    "Сделка не закрыта": "122623",
    "4278": "01_Январь",
    "4280": "02_Февраль",
    "4282": "03_Март",
    "4284": "04_Апрель",
    "4286": "05_Май",
    "4288": "06_Июнь",
    "4290": "07_Июль",
    "4292": "08_Август",
    "4294": "09_Сентябрь",
    "4296": "10_Октябрь",
    "4298": "11_Ноябрь",
    "4300": "12_Декабрь",
}

const renderCellContent = (column, value) => {
  if (column.isLink && value) {
    const href = column.linkTemplate.replace("{value}", value);
    return (
      <Link
        href={href}
        isExternal
        textDecoration={"underline"}
        color="white"
        _hover={{ color: "blue.500" }}
      >
        {value}
      </Link>
    );
  }

  if (column.format) {
    return column.format(value);
  }

  if (column.isNumeric) {
    return Number(value).toLocaleString("ru-RU");
  }

  return value || 0;
};

const profitTable = observer(({ data, renderButtons }) => {
  const toast = useToast();

  const { pageStore } = useStores();
  const [expandedGroups, setExpandedGroups] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: 'profit',
    direction: 'desc',
    level: 'pivot'
  });

  const availableColumns = useMemo(() => {
    const filtered = pageStore.columnConfigProfit.filter(
      col => data.length > 0 && col.active
    );

    const profitColumns = filtered.filter(
      col => col.key === "profit" || col.key === "RevRub"
    );
    const otherColumns = filtered.filter(
      col => col.key !== "profit" && col.key !== "RevRub"
    );

    return [...otherColumns, ...profitColumns];
  }, [data, pageStore.columnConfigProfit]);

  const handleSort = useCallback((key, level) => {
    setSortConfig(prev => {
      if (prev.key === key && prev.level === level) {
        return {
          key,
          level,
          direction: prev.direction === "asc" ? "desc" : "asc"
        };
      }
      return { key, level, direction: "desc" };
    });
  }, []);

  const formatCellValue = useCallback((value, isNumeric = false) => {
    if (value === null || value === undefined || value === "") {
      return "0";
    }

    if (isNumeric) {
      const num = typeof value === 'string'
        ? parseFloat(value.replace(/[^\d.-]/g, ''))
        : Number(value);

      if (!isNaN(num)) {
        return Math.round(num).toLocaleString("ru-RU");
      }
      return "0";
    }

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-");
      return `${day}.${month}.${year}`;
    }

    return value || "";
  }, []);

  const formatDateKey = useCallback((key) => {
    if (key === "Все периоды") return key;

    if (pageStore.year_or_mouth_view === "month") {
      const [year, month] = key.split("-");
      return `${MONTH_NAMES[parseInt(month) - 1]} ${year}`;
    }

    return `${key}`;
  }, [pageStore.year_or_mouth_view]);

  const formatYearMonthWithName = (value) => {
    if (!value) return "—";

    const date =
      typeof value === "number"
        ? new Date(value < 1e12 ? value * 1000 : value) // сек/мс
        : new Date(value);

    if (!Number.isFinite(date.getTime())) return "—";

    const year = date.getFullYear();
    const monthNum = String(date.getMonth() + 1).padStart(2, "0");

    const monthName = new Intl.DateTimeFormat("ru-RU", { month: "long" })
      .format(date)
      .replace(/^./, c => c.toUpperCase());

    return `${year}-${monthNum}_${monthName}`;
  };

  const { groupedData, allTimeKeys, totalSums, totalGlobalSum, totalGlobalSumUF, totalCount, totalUniqueManagers, totalUniqueProducers, totalUniqueCompayns, totalRevenue } = useMemo(() => {
    if (!pageStore.selected_view_profit || !pageStore.year_or_mouth_view) {
      return {
        groupedData: null,
        allTimeKeys: [],
        totalSums: {},
        totalGlobalSum: 0,
        totalGlobalSumUF: 0,
        totalCount: 0,
        totalUniqueManagers: 0,
        totalUniqueProducers: 0,
        totalUniqueCompayns: 0,
        totalRevenue: 0
      };
    }

    const getPrimaryKey = (item) => {
      if (pageStore.selected_view_profit === "manager-deals") return item.fio;
      if (pageStore.selected_view_profit === "manager-partner") return item.fio;
      if (pageStore.selected_view_profit === "partner-manager") return item.name;
      if (pageStore.selected_view_profit === "manager-producer") return item.fio;
      if (pageStore.selected_view_profit === "producer-manager") return item.UF_CRM_1473868262;
      if (pageStore.selected_view_profit === "partner-deals") return item.name;
      if (pageStore.selected_view_profit === "partner-partner") return item.name;
      if (pageStore.selected_view_profit === "departament-manager") return item.departament_name;
      if (pageStore.selected_view_profit === "departament-manager-withoutROP") return item.departament_name
      if (pageStore.selected_view_profit === "producer-producer") return item.UF_CRM_1473868262;
      return "Все";
    };

    const getSecondaryKey = (item) => {
      if (pageStore.selected_view_profit === "manager-partner") return item.name;
      if (pageStore.selected_view_profit === "partner-manager") return item.fio;
      if (pageStore.selected_view_profit === "manager-producer") return item.UF_CRM_1473868262;
      if (pageStore.selected_view_profit === "producer-manager") return item.fio;
      if (pageStore.selected_view_profit === "departament-manager") return item.fio;
      if (pageStore.selected_view_profit === "departament-manager-withoutROP") return item.fio
      return null;
    };

    const grouped = {};
    const timeKeysSet = new Set();

    const now = new Date();
    const currentYear = now.getFullYear() + 1;
    const currentMonth = now.getMonth() + 1; // 1-12

    // Выбранные значения
    const selectedMonths = pageStore.selected_period_month || []; // ["12_Декабрь", "11_Ноябрь"]
    const selectedYears = pageStore.selected_period_years || [];  // [2027, 2026]

    // Преобразуем месяцы в числа
    const selectedMonthNumbers = selectedMonths.map(m => Number(m.split("_")[0]));

    // ✅ Функция получения годов
    function getYears() {
      // 3. Если НИ годы, НИ месяцы не выбраны
      if (!selectedYears.length && !selectedMonthNumbers.length) {
        return [currentYear];
      }

      // 1. Если годы НЕ выбраны — берем все до текущего
      if (!selectedYears.length) {
        const years = [];
        for (let y = 2020; y <= currentYear; y++) years.push(y); // 2020 можешь поменять
        return years;
      }

      return selectedYears;
    }

    function getMonthsForYear(year) {
      // 2. Если месяцы НЕ выбраны
      if (!selectedMonthNumbers.length) {
        // Для текущего года — только до текущего месяца
        if (year === currentYear) {
          return Array.from({ length: currentMonth }, (_, i) => i + 1);
        }
        // Для прошлых лет — все 12
        return Array.from({ length: 12 }, (_, i) => i + 1);
      }

      return selectedMonthNumbers;
    }

    if (pageStore.year_or_mouth_view === "year") {
      const years = getYears();

      years.forEach(year => {
        timeKeysSet.add(String(year));
      });
    }

    // ✅ МЕСЯЧНЫЙ ВИД
    if (pageStore.year_or_mouth_view === "month") {
      const years = getYears();

      years.forEach(year => {
        const months = getMonthsForYear(year);

        months.forEach(month => {
          const title = `${year}-${month}_${MONTH_NAMES[month - 1]}`;
          timeKeysSet.add(title);
        });
      });
    }

    let totalCount = 0;
    const uniqueManagers = new Set();
    const uniqueProducers = new Set();
    const uniqueCompayns = new Set();
    const totalRevenue = 0;


    data.forEach(item => {
      const primaryKey = getPrimaryKey(item) || "Без группы";
      const secondaryKey = getSecondaryKey(item) || "Без подгруппы";

      if ((pageStore.selected_view_profit == "departament-manager-withoutROP") && (item.role_name == "Руководитель отдела продаж" || item.role_name == "Руководитель удалённого отдела продаж")) {
        return;
      }

      if (pageStore.workers && !item.active) return

      if (pageStore.order_num_filter && item.Num && !(item.Num.toLowerCase().includes(pageStore.order_num_filter.toLowerCase()))) return

      if (pageStore.order_deal_filter && item.ID && !(item.ID.toString().includes(pageStore.order_deal_filter))) return

      if (pageStore.isReturn == false && item.IsReturn) return

      let timeKey = "Все периоды";
      if (pageStore.year_or_mouth_view === "month") {
        if (item.closdate && pageStore.month_and_year_fromProfit) {
          timeKey = formatYearMonthWithName(item.closdate);
        } if (item.ClosDate && pageStore.month_and_year_from1C) {
          timeKey = formatYearMonthWithName(item.ClosDate);
        } if (item.UF_CRM_1490281786 && pageStore.month_and_year_fromB24) {
          timeKey = `${YEARS_MAP[item.UF_CRM_1490282792] || "Без года"}-${MONTH_MAP_B24[item.UF_CRM_1490281786] || "Без месяца"}`;
        }
      } else if (pageStore.year_or_mouth_view === "year") {
        if (item.closdate && pageStore.month_and_year_fromProfit) {
          timeKey = `${new Date(item.closdate * 1000).getFullYear()}`;
        } if (item.ClosDate && pageStore.month_and_year_from1C) {
          timeKey = `${new Date(item.ClosDate).getFullYear()}`;
        } if (item.UF_CRM_1490282792 && pageStore.month_and_year_fromB24) {
          timeKey = `${YEARS_MAP[item.UF_CRM_1490282792] || "Без года"}`;
        }
      }

      /*
      if (!((item.UF_CRM_1490282792 == currentYear) && (parseInt(item?.UF_CRM_1490281786?.split("_")[0], 10) > currentMonth))) {
        timeKeysSet.add(timeKey);
      }
        */
      totalCount++;
      if (item.fio) uniqueManagers.add(item.fio);
      if (item.UF_CRM_1473868262) uniqueProducers.add(item.UF_CRM_1473868262);
      if (item.name) uniqueCompayns.add(item.name);

      if (!grouped[primaryKey]) {
        grouped[primaryKey] = {
          timeGroups: {},
          totalSum: 0,
          totalSumUF: 0,
          subGroups: {},
          count: 0,
          uniqueManagers: new Set(),
          uniqueProducers: new Set(),
          uniqueCompayns: new Set(),
          totalRevenue: 0
        };
      }

      if (!grouped[primaryKey].subGroups[secondaryKey]) {
        grouped[primaryKey].subGroups[secondaryKey] = {
          items: [],
          timeGroups: {},
          totalSum: 0,
          totalSumUF: 0,
          count: 0,
          uniqueManagers: new Set(),
          uniqueProducers: new Set(),
          uniqueCompayns: new Set(),
          totalRevenue: 0
        };
      }

      const subGroup = grouped[primaryKey].subGroups[secondaryKey];
      if (!["partner-partner", "producer-producer"].includes(pageStore.selected_view_profit)) {
        subGroup.items.push(item);
      }
      subGroup.count++;
      if (item.fio) subGroup.uniqueManagers.add(item.fio);
      if (item.UF_CRM_1473868262) subGroup.uniqueProducers.add(item.UF_CRM_1473868262);
      if (item.name) subGroup.uniqueCompayns.add(item.name);
      if (["partner-manager", "partner-partner", "partner-deals"].includes(pageStore.selected_view_profit)) {
        if (item.revenue) subGroup.totalRevenue = item.revenue;
      }

      if (!subGroup.timeGroups[timeKey]) {
        subGroup.timeGroups[timeKey] = { items: [], sum: 0, sumUF: 0, count: 0 };
      }

      if (!["partner-partner", "producer-producer"].includes(pageStore.selected_view_profit)) {
        subGroup.timeGroups[timeKey].items.push(item);
      }
      subGroup.timeGroups[timeKey].sum += Math.round(parseFloat(item.profit)) || 0;
      subGroup.timeGroups[timeKey].sumUF += Math.round(parseFloat(item.RevRub)) || 0;
      subGroup.timeGroups[timeKey].count++;
      subGroup.totalSum += Math.round(parseFloat(item.profit)) || 0;
      subGroup.totalSumUF += Math.round(parseFloat(item.RevRub)) || 0;

      if (!grouped[primaryKey].timeGroups[timeKey]) {
        grouped[primaryKey].timeGroups[timeKey] = { sum: 0, sumUF: 0, count: 0 };
      }
      grouped[primaryKey].timeGroups[timeKey].sum += Math.round(parseFloat(item.profit)) || 0;
      grouped[primaryKey].timeGroups[timeKey].sumUF += Math.round(parseFloat(item.RevRub)) || 0;
      grouped[primaryKey].timeGroups[timeKey].count++;
      grouped[primaryKey].totalSum += Math.round(parseFloat(item.profit)) || 0;
      grouped[primaryKey].totalSumUF += Math.round(parseFloat(item.RevRub)) || 0;
      grouped[primaryKey].count++;
      if (item.fio) grouped[primaryKey].uniqueManagers.add(item.fio);
      if (item.UF_CRM_1473868262) grouped[primaryKey].uniqueProducers.add(item.UF_CRM_1473868262);
      if (item.name) grouped[primaryKey].uniqueCompayns.add(item.name);
      if (["partner-manager", "partner-partner", "partner-deals"].includes(pageStore.selected_view_profit)) {
        if (item.revenue) grouped[primaryKey].totalRevenue = item.revenue;
      }
    });

    function sortDateArray(arr) {
      return arr.sort((a, b) => {
        const isAInvalid = a.startsWith("Сделка не закрыта");
        const isBInvalid = b.startsWith("Сделка не закрыта");

        // ✅ "Сделка не закрыта" всегда внизу
        if (isAInvalid && !isBInvalid) return 1;
        if (!isAInvalid && isBInvalid) return -1;
        if (isAInvalid && isBInvalid) return 0;

        // ✅ Парсим "2025-06_Июнь"
        const [aYM] = a.split("_");
        const [bYM] = b.split("_");

        const [aYear, aMonth] = aYM.split("-").map(Number);
        const [bYear, bMonth] = bYM.split("-").map(Number);

        // ✅ Сравниваем по дате
        return aYear !== bYear
          ? aYear - bYear
          : aMonth - bMonth;
      });
    }

    function normalizeKey(key) {
      if (key.startsWith("Сделка не закрыта")) return key;
      if (pageStore.year_or_mouth_view === "year") {
        return key
      }

      const [ym, name] = key.split("_");
      let [year, month] = ym.split("-");

      month = month.padStart(2, "0"); // ← ВАЖНО

      return `${year}-${month}_${name}`;
    }

    const allTimeKeys = sortDateArray(
      Array.from(
        new Set(
          Array.from(timeKeysSet)
            .filter(k => k !== "Все периоды")
            .map(normalizeKey)   // ✅ нормализация
        )
      )
    );


    const groupedArray = Object.entries(grouped).map(([primaryKey, group]) => {
      const subGroups = Object.entries(group.subGroups).map(([secondaryKey, subGroup]) => ({
        secondaryKey,
        ...subGroup,
        uniqueManagersCount: subGroup.uniqueManagers.size,
        uniqueProducersCount: subGroup.uniqueProducers.size,
        uniqueCompaynsCount: subGroup.uniqueCompayns.size,
        totalRevenue: subGroup.totalRevenue
      }));

      const hasOnlyDefaultSubgroup = subGroups.length === 1 && subGroups[0].secondaryKey === "Без подгруппы";

      return {
        primaryKey,
        timeGroups: group.timeGroups,
        totalSum: group.totalSum,
        totalSumUF: group.totalSumUF,
        count: group.count,
        uniqueManagersCount: group.uniqueManagers.size,
        uniqueProducersCount: group.uniqueProducers.size,
        uniqueCompaynsCount: group.uniqueCompayns.size,
        totalRevenue: group.totalRevenue,
        subGroups: hasOnlyDefaultSubgroup
          ? [{ ...subGroups[0], secondaryKey: null, autoExpanded: true }]
          : subGroups
      };
    });

    const totalSums = {};
    const totalSumsUF = {};
    const totalCounts = {};
    let totalGlobalSum = 0;
    let totalGlobalSumUF = 0;

    groupedArray.forEach(group => {
      totalGlobalSum += group.totalSum;
      totalGlobalSumUF += group.totalSumUF;
      Object.entries(group.timeGroups).forEach(([timeKey, { sum, sumUF, count }]) => {
        totalSums[timeKey] = (totalSums[timeKey] || 0) + sum;
        totalSumsUF[timeKey] = (totalSumsUF[timeKey] || 0) + sumUF;
        totalCounts[timeKey] = (totalCounts[timeKey] || 0) + count;
      });
    });

    return {
      groupedData: groupedArray,
      allTimeKeys,
      totalSums,
      totalSumsUF,
      totalCounts,
      totalGlobalSum,
      totalGlobalSumUF,
      totalCount,
      totalUniqueManagers: uniqueManagers.size,
      totalUniqueProducers: uniqueProducers.size,
      totalUniqueCompayns: uniqueCompayns.size,
      totalRevenue: totalRevenue
    };
  }, [data, pageStore.selected_view_profit, pageStore.year_or_mouth_view, pageStore.columnConfigProfit, pageStore.workers, pageStore.order_num_filter, pageStore.order_deal_filter, pageStore.isReturn]);

  const getSortValue = useCallback((item, key, level) => {
    if (level === 'pivot') {
      switch (key) {
        case 'group-name':
          return item.primaryKey || item.secondaryKey || '';
        case 'group-total':
        case 'profit':
          return item.totalSum || 0;
        case 'RevRub':
          return item.totalSumUF || 0;
        case 'TITLE':
          return item.count || 0;
        case 'fio':
          return item.uniqueManagersCount || 0;
        case 'UF_CRM_1473868262':
          return item.uniqueProducersCount || 0;
        case "revenue":
          return item.totalRevenue || 0
        default:
          if (key.startsWith('period||')) {
            const periodKey = key.split('||');
            if (!item.timeGroups) return 0;
            const periodValue = item.timeGroups[periodKey[2]];
            const prevKey = allTimeKeys[allTimeKeys.indexOf(periodKey[2]) - 1]
            const prevPeriodValue = item.timeGroups[prevKey]
            if (typeof periodValue === 'object' && periodValue !== null) {
              if (periodKey[1] == 'abs') return periodValue.sum || 0;
              if (periodKey[1] == 'percent') return prevPeriodValue ? (periodValue.sum - prevPeriodValue?.sum) / prevPeriodValue?.sum * 100 : 0;
              if (periodKey[1] == 'part') return periodValue.sum / (totalSums[periodKey[2]] || 0) * 100
            }
            return periodValue || 0;
          }
          return 0;
      }
    } else {
      if (key.startsWith('period||')) {
        const periodKey = key.split('||');
        let itemPeriodKey = "Все периоды";
        if (pageStore.year_or_mouth_view === "month") {
          if (item.closdate) {
            itemPeriodKey = formatYearMonthWithName(item.closdate);
          }
        } else if (pageStore.year_or_mouth_view === "year") {
          if (item.closdate) {
            itemPeriodKey = `${new Date(item.closdate * 1000).getFullYear()}`;

          }
        }
        if (itemPeriodKey === periodKey[2]) {
          if (periodKey[1] == "abs") return parseFloat(item.profit) || 0;
          if (periodKey[1] == "percent") return parseFloat(item.profit) || 0;
          if (periodKey[1] == "part") return parseFloat(item.profit / (totalSums[periodKey[2]] || 0) * 100)
        }
        return 0;
      } else {
        const value = item[key];
        const column = availableColumns.find(c => c.key === key);
        if (column?.isNumeric) {
          return parseFloat(value) || 0;
        }
        return value || "";
      }
    }
  }, [allTimeKeys.length, pageStore.year_or_mouth_view, availableColumns]);

  const sortedGroupedData = useMemo(() => {
    if (!groupedData) return null;

    const compareItems = (a, b, key, level) => {
      let valueA = getSortValue(a, key, level);
      let valueB = getSortValue(b, key, level);

      const numA = parseFloat(valueA);
      const numB = parseFloat(valueB);

      // Если оба значения удалось преобразовать в число — сравниваем как числа
      if (!isNaN(numA) && !isNaN(numB)) {
        return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
      }

      // Если хотя бы одно значение не число — оставляем строковое сравнение
      return sortConfig.direction === 'asc'
        ? String(valueA).localeCompare(String(valueB))
        : String(valueB).localeCompare(String(valueA));
    };

    const sortedGroups = [...groupedData].sort((a, b) =>
      compareItems(a, b, sortConfig.key, 'pivot'));

    return sortedGroups.map(group => ({
      ...group,
      subGroups: group.subGroups
        .sort((a, b) => compareItems(a, b, sortConfig.key, 'pivot'))
        .map(subGroup => ({
          ...subGroup,
          items: [...subGroup.items].sort((a, b) =>
            compareItems(a, b, sortConfig.key, 'detail'))
        }))
    }));
  }, [groupedData, sortConfig, getSortValue]);

  const handleExportToXLSX = useCallback(async () => {
    var can = await pageStore.CheckPermission("export excel")
    if (!can) {
      toast({
        title: "У вас нет права экспорта в excel",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return
    }
    const table = document.querySelector("table");
    if (!table) return;

    const workbook = createWorkbookFromTable(table);

    // Сохраняем файл
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "table.xlsx");
  }, [groupedData, allTimeKeys, totalSums, totalGlobalSum, totalGlobalSumUF, availableColumns, formatCellValue, formatDateKey, pageStore.year_or_mouth_view]);

  const toggleExpand = useCallback((type, key, subKey = null) => {
    const compositeKey = subKey ? `${key}__${subKey}` : key;
    setExpandedGroups(prev => ({
      ...prev,
      [compositeKey]: !prev[compositeKey]
    }));
  }, []);

  const expandAll = useCallback(() => {
    const newExpanded = {};
    groupedData?.forEach(group => {
      newExpanded[group.primaryKey] = true;
      group.subGroups.forEach(subGroup => {
        newExpanded[`${group.primaryKey}__${subGroup.secondaryKey ?? "auto"}`] = true;
      });
    });
    setExpandedGroups(newExpanded);
  }, [groupedData]);

  const collapseAll = useCallback(() => {
    setExpandedGroups({});
  }, []);

  const renderGroupCellContent = useCallback((column, group, isSubGroup = false) => {
    const data = isSubGroup ? group : group;

    switch (column.key) {
      case "TITLE":
        return `${data.count}`;
      case "fio":
        return `${data.uniqueManagersCount}`;
      case "UF_CRM_1473868262":
        return `${data.uniqueProducersCount}`;
      case "name":
        return `${data.uniqueCompaynsCount}`;
      case "revenue":
        return `${data.totalRevenue}`
      default:
        return "";
    }
  }, []);

  const renderProfitColumns = () =>
    pageStore.year_or_mouth_view &&
    allTimeKeys.map(timeKey => (
      <React.Fragment key={timeKey}>
        {pageStore.absolute_column_profit && (
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort(`period||abs||${timeKey}`, 'pivot')}
            cursor="pointer"
            py={pageStore.rowHeight}
          >
            {formatDateKey(timeKey)}
            {sortConfig.key === `period||abs||${timeKey}` &&
              sortConfig.level === 'pivot' && (
                <Box as="span" ml={2}>
                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                </Box>
              )}
          </Th>
        )}

        {pageStore.percent_column_profit && (
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort(`period||percent||${timeKey}`, 'pivot')}
            cursor="pointer"
            py={pageStore.rowHeight}
          >
            {pageStore.absolute_column_profit
              ? "%"
              : `${formatDateKey(timeKey)} %`}
            {sortConfig.key === `period||percent||${timeKey}` &&
              sortConfig.level === 'pivot' && (
                <Box as="span" ml={2}>
                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                </Box>
              )}
          </Th>
        )}

        {pageStore.part_column_profit && (
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort(`period||part||${timeKey}`, 'pivot')}
            cursor="pointer"
            py={pageStore.rowHeight}
          >
            Доли %
            {sortConfig.key === `period||part||${timeKey}` &&
              sortConfig.level === 'pivot' && (
                <Box as="span" ml={2}>
                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                </Box>
              )}
          </Th>
        )}
      </React.Fragment>
    ));

  const renderTableHeader = () => (
    <Thead bgColor={"black"} position={"sticky"} top={"0px"} zIndex={11}>
      <Tr>
        <Th color="white" px={0} />
        <Th
          color="white"
          onClick={() => handleSort('group-name', 'pivot')}
          cursor="pointer"
          py={pageStore.rowHeight}
        >
          Группа
          {sortConfig.key === 'group-name' && sortConfig.level === 'pivot' && (
            <Box as="span" ml={2}>
              {sortConfig.direction === "asc" ? "↑" : "↓"}
            </Box>
          )}
        </Th>
        {availableColumns
          .filter(col => col.key !== "profit" && col.key !== "RevRub")
          .map(column => (
            <Th
              key={column.key}
              color="white"
              px={4}
              py={pageStore.rowHeight}
              textAlign={column.isNumeric ? "right" : "left"}
              onClick={() => handleSort(column.key, column.key === 'TITLE' || column.key === 'fio' || column.key === 'UF_CRM_1473868262' || column.key === "revenue" ? 'pivot' : 'detail')}
              cursor="pointer"
            >
              {column.title}
              {sortConfig.key === column.key && (
                <Box as="span" ml={2}>
                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                </Box>
              )}
            </Th>
          ))}
        {renderProfitColumns()}
        <Th
          color="white"
          textAlign="right"
          onClick={() => handleSort('RevRub', 'pivot')}
          cursor="pointer"
          py={pageStore.rowHeight}
        >
          Итоговая сумма
          {sortConfig.key === 'RevRub' && sortConfig.level === 'pivot' && (
            <Box as="span" ml={2}>
              {sortConfig.direction === "asc" ? "↑" : "↓"}
            </Box>
          )}
        </Th>
        <Th
          color="white"
          textAlign="right"
          onClick={() => handleSort('profit', 'pivot')}
          cursor="pointer"
          py={pageStore.rowHeight}
        >
          Прибыль 1 (М)
          {sortConfig.key === 'profit' && sortConfig.level === 'pivot' && (
            <Box as="span" ml={2}>
              {sortConfig.direction === "asc" ? "↑" : "↓"}
            </Box>
          )}
        </Th>
      </Tr>
    </Thead>
  );

  const renderTotalRow = () => {
    return (
      <Tr bg="#555">
        <Td px={0} py={0} />
        <Td fontWeight="bold" color="white" py={pageStore.rowHeight}>Итого {sortedGroupedData.length}</Td>
        {availableColumns
          .filter(col => col.key !== "profit" && col.key !== "RevRub")
          .map(column => {
            let content = "";
            switch (column.key) {
              case "TITLE":
                content = `${totalCount}`;
                break;
              case "fio":
                content = `${totalUniqueManagers}`;
                break;
              case "UF_CRM_1473868262":
                content = `${totalUniqueProducers}`;
                break;
              case "name":
                content = `${totalUniqueCompayns}`;
                break;
              default:
                content = "";
            }
            return (
              <Td key={column.key} px={4} py={2} textAlign={column.isNumeric ? "right" : "left"}>
                {content}
              </Td>
            );
          })}
        {pageStore.year_or_mouth_view &&
          allTimeKeys.map((timeKey, i) => {
            const current = totalSums[timeKey] ?? 0;
            const prevKey = allTimeKeys[i - 1];
            const prev = prevKey ? totalSums[prevKey] ?? 0 : 0;

            return (
              <React.Fragment key={timeKey}>
                {pageStore.absolute_column_profit && (
                  <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                    {formatCellValue(current, true)}
                  </Td>
                )}

                {pageStore.percent_column_profit && (() => {
                  if (prev === 0) {
                    return (
                      <Td textAlign="right" fontWeight="bold" color="gray.400" py={pageStore.rowHeight}>
                        —
                      </Td>
                    );
                  }

                  const ratio = (current - prev) / prev; // относительное изменение
                  const percent = ratio * 100;

                  let color = "white";
                  let sign = "";

                  if (ratio < 0) {
                    color = "red.400";
                    sign = "−";
                  } else if (ratio < 0.2) {
                    color = "yellow.400";
                    sign = "+";
                  } else {
                    color = "green.300"; // яркосалатовый
                    sign = "+";
                  }

                  return (
                    <Td textAlign="right" fontWeight="bold" color={color} py={pageStore.rowHeight}>
                      {sign}
                      {formatCellValue(Math.abs(percent), true)}
                    </Td>
                  );
                })()}

                {pageStore.part_column_profit && (
                  <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                    {formatCellValue("100%", false)}
                  </Td>
                )}
              </React.Fragment>
            );
          })}
        <Td textAlign="right" fontWeight="bold" color="rgb(100,240,10)" py={pageStore.rowHeight}>
          {formatCellValue(totalGlobalSumUF, true)}
        </Td>
        <Td textAlign="right" fontWeight="bold" color="rgb(15, 175, 250)" py={pageStore.rowHeight}>
          {formatCellValue(totalGlobalSum, true)}
        </Td>
      </Tr>
    );
  };

  const renderGroupRow = (group) => {
    const isExpanded = expandedGroups[group.primaryKey];

    return (
      <React.Fragment key={group.primaryKey}>
        <Tr bg="#333">
          <Td px={0} py={0}>
            <Button
              size="sm"
              fontSize="20px"
              bg="#333"
              color="white"
              _hover={{ bg: "#222" }}
              onClick={() => toggleExpand('group', group.primaryKey)}
            >
              {isExpanded ? "−" : "+"}
            </Button>
          </Td>
          <Td
            cursor="pointer"
            onClick={() => toggleExpand('group', group.primaryKey)}
            fontWeight="bold"
            color="white"
            py={pageStore.rowHeight}
          >
            {group.primaryKey}
          </Td>
          {availableColumns
            .filter(col => col.key !== "profit" && col.key !== "RevRub")
            .map(column => (
              <Td
                key={column.key}
                px={4}
                py={pageStore.rowHeight}
                textAlign={column.isNumeric ? "right" : "left"}
              >
                {renderGroupCellContent(column, group)}
              </Td>
            ))}
          {pageStore.year_or_mouth_view &&
            allTimeKeys.map((timeKey, i) => {
              const current = group.timeGroups[timeKey]?.sum ?? 0;
              const prevKey = allTimeKeys[i - 1];
              const prev = prevKey ? group.timeGroups[prevKey]?.sum ?? 0 : 0;

              return (
                <React.Fragment key={timeKey}>
                  {pageStore.absolute_column_profit && (
                    <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                      {formatCellValue(current, true)}
                    </Td>
                  )}

                  {pageStore.percent_column_profit && (() => {
                    if (prev === 0) {
                      return (
                        <Td textAlign="right" fontWeight="bold" color="gray.400" py={pageStore.rowHeight}>
                          —
                        </Td>
                      );
                    }

                    const ratio = (current - prev) / prev; // относительное изменение
                    const percent = ratio * 100;

                    let color = "white";
                    let sign = "";

                    if (ratio < 0) {
                      color = "red.400";
                      sign = "−";
                    } else if (ratio < 0.2) {
                      color = "yellow.400";
                      sign = "+";
                    } else {
                      color = "green.300"; // яркосалатовый
                      sign = "+";
                    }

                    return (
                      <Td textAlign="right" fontWeight="bold" color={color} py={pageStore.rowHeight}>
                        {sign}
                        {formatCellValue(Math.abs(percent), true)}
                      </Td>
                    );
                  })()}

                  {pageStore.part_column_profit && (
                    <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                      {formatCellValue(current / (totalSums[timeKey] || 0) * 100, true)}
                    </Td>
                  )}
                </React.Fragment>
              );
            })}
          <Td textAlign="right" fontWeight="bold" color="rgb(100,240,10)" py={pageStore.rowHeight}>
            {formatCellValue(group.totalSumUF, true)}
          </Td>
          <Td textAlign="right" fontWeight="bold" color="rgb(15, 175, 250)" py={pageStore.rowHeight}>
            {formatCellValue(group.totalSum, true)}
          </Td>
        </Tr>

        {isExpanded && group.subGroups.map(renderSubGroupRow(group))}
      </React.Fragment>
    );
  };

  const renderSubGroupRow = (group) => (subGroup) => {
    const compositeKey = `${group.primaryKey}__${subGroup.secondaryKey ?? "auto"}`;
    const isExpanded = expandedGroups[compositeKey] || subGroup.autoExpanded;

    return (
      <React.Fragment key={compositeKey}>
        {subGroup.secondaryKey && (
          <Tr bg="#444">
            <Td px={0} py={0}>
              <Button
                color="white"
                fontSize="25px"
                _hover={{ bg: "black", color: "white" }}
                size="sm"
                bg="#444"
                onClick={() => toggleExpand('subgroup', group.primaryKey, subGroup.secondaryKey)}
              >
                {isExpanded ? "−" : "+"}
              </Button>
            </Td>
            <Td
              pl={12}
              cursor="pointer"
              onClick={() => toggleExpand('subgroup', group.primaryKey, subGroup.secondaryKey)}
              py={pageStore.rowHeight}
              fontWeight="bold"
            >
              {subGroup.secondaryKey}
            </Td>
            {availableColumns
              .filter(col => col.key !== "profit" && col.key !== "RevRub")
              .map(column => (
                <Td
                  key={column.key}
                  px={4}
                  py={pageStore.rowHeight}
                  textAlign={column.isNumeric ? "right" : "left"}
                >
                  {renderGroupCellContent(column, subGroup, true)}
                </Td>
              ))}
            {pageStore.year_or_mouth_view &&
              allTimeKeys.map((timeKey, i) => {
                const current = subGroup.timeGroups[timeKey]?.sum ?? 0;
                const prevKey = allTimeKeys[i - 1];
                const prev = prevKey ? subGroup.timeGroups[prevKey]?.sum ?? 0 : 0;

                return (
                  <React.Fragment key={timeKey}>
                    {pageStore.absolute_column_profit && (
                      <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                        {formatCellValue(current, true)}
                      </Td>
                    )}

                    {pageStore.percent_column_profit && (() => {
                      if (prev === 0) {
                        return (
                          <Td textAlign="right" fontWeight="bold" color="gray.400" py={pageStore.rowHeight}>
                            —
                          </Td>
                        );
                      }

                      const ratio = (current - prev) / prev; // относительное изменение
                      const percent = ratio * 100;

                      let color = "white";
                      let sign = "";

                      if (ratio < 0) {
                        color = "red.400";
                        sign = "−";
                      } else if (ratio < 0.2) {
                        color = "yellow.400";
                        sign = "+";
                      } else {
                        color = "green.300"; // яркосалатовый
                        sign = "+";
                      }

                      return (
                        <Td textAlign="right" fontWeight="bold" color={color} py={pageStore.rowHeight}>
                          {sign}
                          {formatCellValue(Math.abs(percent), true)}
                        </Td>
                      );
                    })()}

                    {pageStore.part_column_profit && (
                      <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                        {formatCellValue(current / (totalSums[timeKey] || 0) * 100, true)}
                      </Td>
                    )}
                  </React.Fragment>
                );
              })}
            <Td textAlign="right" fontWeight="bold" py={pageStore.rowHeight}>
              {formatCellValue(subGroup.totalSumUF, true)}
            </Td>
            <Td textAlign="right" fontWeight="bold" py={pageStore.rowHeight}>
              {formatCellValue(subGroup.totalSum, true)}
            </Td>
          </Tr>
        )}

        {isExpanded && subGroup.items.map((item, idx) =>
          renderDetailRow(item, idx, subGroup.items.length)
        )}
      </React.Fragment>
    );
  };

  const renderDetailRow = (item, index, totalItems) => {
    let rowTimeKey = "Все периоды";
    if (pageStore.year_or_mouth_view === "month") {
      if (item.closdate) {
        rowTimeKey = formatYearMonthWithName(item.closdate);
      }
    } else if (pageStore.year_or_mouth_view === "year") {
      if (item.closdate) {
        rowTimeKey = `${new Date(item.closdate * 1000).getFullYear()}`;

      }
    }

    return (
      <Tr key={index} bg={index % 2 === 0 ? "#555" : "#666"}>
        <Td px={4} py={pageStore.rowHeight} />
        <Td px={4} py={pageStore.rowHeight} />
        {availableColumns
          .filter(col => col.key !== "profit" && col.key !== "RevRub")
          .map(column => (
            <Td
              key={column.key}
              px={4}
              py={pageStore.rowHeight}
              textAlign={column.isNumeric ? "right" : "left"}
              fontStyle={item.active ? "normal" : "italic"}
            >
              {renderCellContent(column, item[column.key])}
            </Td>
          ))}
        {pageStore.year_or_mouth_view && pageStore.absolute_column_profit && allTimeKeys.map(timeKey => (
          <Td key={timeKey} px={4} py={pageStore.rowHeight} textAlign="right">
            {timeKey === rowTimeKey ? formatCellValue(item.profit, true) : "0"}
          </Td>
        ))}
        {pageStore.year_or_mouth_view && pageStore.percent_column_profit && allTimeKeys.map(timeKey => (
          <Td key={timeKey} px={4} py={pageStore.rowHeight} textAlign="right">
            {"—"}
          </Td>
        ))}

        {pageStore.year_or_mouth_view && pageStore.part_column_profit && allTimeKeys.map(timeKey => (
          <Td key={timeKey} px={4} py={pageStore.rowHeight} textAlign="right">
            {timeKey === rowTimeKey ? formatCellValue(item.profit / (totalSums[timeKey] || 0) * 100, true) : "0"}
          </Td>
        ))}
        <Td px={4} py={pageStore.rowHeight} textAlign="right">
          {formatCellValue(item.RevRub, true)}
        </Td>
        <Td px={4} py={pageStore.rowHeight} textAlign="right">
          {formatCellValue(item.profit, true)}
        </Td>
      </Tr>
    );
  };

  const renderFlatTable = () => (
    <Table tableLayout="fixed" width="100%" minWidth="800px" variant="simple" bg="" color="white">
      <Thead>
        <Tr>
          {availableColumns.map(column => (
            <Th
              key={column.key}
              color="white"
              onClick={() => handleSort(column.key, 'pivot')}
              cursor="pointer"
              px={4}
              py={pageStore.rowHeight}
              textAlign={column.isNumeric ? "right" : "left"}
            >
              {column.title}
              {sortConfig.key === column.key && sortConfig.level === 'pivot' && (
                <Box as="span" ml={2}>
                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                </Box>
              )}
            </Th>
          ))}
        </Tr>
      </Thead>
      <Tbody>
        {data.map((row, index) => (
          <Tr key={index} bg={index % 2 === 0 ? "#444" : "#333"}>
            {availableColumns.map(column => (
              <Td
                key={column.key}
                px={4}
                py={pageStore.rowHeight}
                textAlign={column.isNumeric ? "right" : "left"}
                fontStyle={row.active ? "normal" : "italic"}
              >
                {renderCellContent(column, row[column.key])}
              </Td>
            ))}
          </Tr>
        ))}
      </Tbody>
    </Table>
  );

  const buttons = (
    <>
      <Button onClick={handleExportToXLSX} bg="#333" color="white" width="15vw" _hover={{ bg: "#444" }} mr={3}>
        Экспорт в Excel
      </Button>
      <Button onClick={expandAll} bg="#333" color="white" width="15vw" _hover={{ bg: "#444" }} mr={3}>
        Развернуть всё
      </Button>
      <Button onClick={collapseAll} bg="#333" color="white" width="15vw" _hover={{ bg: "#444" }} mr={3}>
        Свернуть всё
      </Button>
    </>
  )

  return (
    <>
      <Collapse in={pageStore.open_filter} animateOpacity>
        {renderButtons ? renderButtons(buttons) : buttons}
      </Collapse>

      {pageStore.data_profit.length != 0 ?
        <Box w="100%">

          <ColumnFilters />

          <Box maxHeight="95vh" overflowX="auto" borderRadius="md" borderWidth={1} borderColor="gray.300">
            {groupedData ? (
              <Table tableLayout="auto" variant="simple" bg="" color="white" width="auto" maxWidth="fit-content">
                {renderTableHeader()}
                <Tbody >
                  {renderTotalRow()}
                  {sortedGroupedData?.map(renderGroupRow)}
                </Tbody>
              </Table>
            ) : (
              renderFlatTable()
            )}
          </Box>
        </Box>
        : null}
    </>
  );
});

export default profitTable;